import React, { useEffect, useState } from 'react';
import AppleDeveloperLogo from '../../../assets/img/logos/apple-developer.png';
import GooglePlayLogo from '../../../assets/img/logos/googleplay.svg';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../contexts/user.context';
import { availablePlans, PLATFORMS, storeStatus, planPeriodicity, subscriptionStatus, plans } from '../../../utils/constants';
import BillingLayout from '../../../layouts/billing';
import { usePlan } from '../../../contexts/plan.context';
import _ from 'lodash';
import PlanCard from './PlanCard';
import PeriodSelector from './PeriodSelector';
import AuthorizationModal from '../../Onboarding/Connect/AuthorizationModal';
import { getPendingPlatformAuth } from '../../../utils/platformAuth';
import wixLogo from '../../../assets/img/platforms/wix.png'
import config from '../../../utils/config';
import { mobilizeiSubscriptionsService } from '../../../services/mobilizei/subscriptions.service';
import { toast } from 'react-toastify';
import { trackMetaEvent, metaPixelEvents } from '../../../utils/metaPixel';
import { trackGtagEvent, gtagEvents } from '../../../utils/gtag';

const useStyles = makeStyles(() => ({
    warning: {
        fontWeight: 700,
        marginBottom: 15
    },
    disclaimer: {
        color: '#858585',
        fontSize: 16,
        padding: '0 16px 40px 16px',
        textAlign: 'center',
        "& p": {
            marginBottom: 10
        },
        "& img": {
            display: 'block',
            margin: '1em auto 0',
            height: 'auto',
            verticalAlign: 'middle',
            '@media (min-width: 960px)': {
                display: 'inline-block',
                margin: '0 0 0 30px'
            }
        },
        "& .dev-account-wrapper": {
            display: 'block',
            justifyContent: 'center',
            alignItems: 'center',
            '@media (min-width: 960px)': {
                display: 'inline-block',
            }
        },
        "& .dev-account": {
            display: 'block',
            "& span": {
                marginLeft: 5
            },
            '@media (min-width: 960px)': {
                display: 'inline-block',
            }
        }
    },
    plansGrid: {
        display: "flex",
        maxWidth: "100%",
        padding: "30px 20px",
        overflowX: 'auto'
    },
    wixLogo: {
        height: '100%',
        width: '100%',
        maxWidth: 500
    },
}));

// The plan selected by a store that has to authorize the app on its platform first,
// kept until the merchant comes back from the authorization signed in again.
const PENDING_PLAN_KEY = 'pending_plan';

export default function Pricing({ fullScreen }) {
    const navigate = useNavigate();

    const pricingRedirections = localStorage.getItem('pricingRedirections');

    const { setPlan } = usePlan();

    const { user, setUser } = useUser();
    const classes = useStyles();

    const [periodicity, setPeriodicity] = useState(planPeriodicity.MONTHLY);
    const [pendingInvoice, setPendingInvoice] = useState([])
    const [loadingPendingInvoice, setLoadingPendingInvoice] = useState(true)
    const [loadingActivateSubscription, setLoadingActivateSubscription] = useState(false)
    const [authorizationState, setAuthorizationState] = useState('closed')

    // Tray/Bagy stores created before the authorization moved to the onboarding may
    // have never authorized the app, and the app needs it to tag the orders and the
    // partner. So they authorize it before hiring a plan, like the new stores did.
    const needsPlatformAuthorization =
        [PLATFORMS.TRAY, PLATFORMS.BAGY].includes(user?.store?.platformId) &&
        !user?.store?.isAuthorizedOnPlatform;

    // Coming back from the authorization, the hiring goes on with the plan selected
    // before the redirect.
    useEffect(() => {
        const pendingPlan = sessionStorage.getItem(PENDING_PLAN_KEY);

        // The authorization may still be going on, when it is retried after the
        // merchant signs in to the platform admin
        if (!pendingPlan || (needsPlatformAuthorization && getPendingPlatformAuth())) return;

        sessionStorage.removeItem(PENDING_PLAN_KEY);

        if (!needsPlatformAuthorization && plans[pendingPlan]) {
            setPlan(pendingPlan);
            navigate('/hire');
        }
    }, [])

    useEffect(() => {
        if (user?.store?.status === storeStatus.ACTIVE) {
            navigate('/')
            return;
        }
    }, [user]);

    useEffect(() => {
        if (pricingRedirections && pricingRedirections > 0) {
            navigate('/')
        } else {
            setTimeout(() => {
                if (user?.store?.platformId === PLATFORMS.WIX && pricingRedirections) {
                    localStorage.setItem('pricingRedirections', 1);
                    window.open(config.WIX_CHECKOUT_URL, "_self")
                }
            }, 3000)
        }
    }, [])

    useEffect(() => {
        const handlePendingInvoice = async () => {
            if (user?.store.status === storeStatus.SUSPENDED) {
                const subscription = await mobilizeiSubscriptionsService.getById(user?.subscription?.id);
                const pendingInvoice = subscription.invoices ? subscription.invoices.filter(invoice => invoice.status === 'pending') : [];
                setPendingInvoice(pendingInvoice)
                setLoadingPendingInvoice(false)
                return subscription.invoices;
            }
        }

        handlePendingInvoice()
    }, [])

    useEffect(() => {
        const showingPlanCards =
            user?.store?.platformId !== PLATFORMS.WIX &&
            user?.store?.status !== storeStatus.SUSPENDED;

        if (showingPlanCards) {
            trackMetaEvent(metaPixelEvents.VIEW_CONTENT, { content_name: 'pricing_plans' });
            trackGtagEvent(gtagEvents.VIEW_ITEM, { item_list_name: 'pricing_plans' });
        }
    }, [])

    const handleNavigation = async (planName) => {
        if (needsPlatformAuthorization) {
            sessionStorage.setItem(PENDING_PLAN_KEY, planName);
            setAuthorizationState('open');
            return;
        }

        navigate('/hire')
    }

    const closeAuthorizationModal = () => {
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        setAuthorizationState('closed');
    }

    const handlePlanSelect = (planType) => {
        const planName = availablePlans[planType][periodicity]

        if (planName) {
            trackMetaEvent(metaPixelEvents.INITIATE_CHECKOUT, {
                content_name: planName,
                currency: 'BRL',
                value: plans[planName].totalPrice
            })
            trackGtagEvent(gtagEvents.BEGIN_CHECKOUT, {
                currency: 'BRL',
                value: plans[planName].totalPrice,
                items: [{ item_name: planName }]
            })
            setPlan(planName)
            handleNavigation(planName)
        }
    }

    const activeSubscription = async () => {
        setLoadingActivateSubscription(true)
        mobilizeiSubscriptionsService.activate(user.store.subscriptionId)
            .then(async (response) => {
                const lastInvoice = response.invoices[0]
                if (lastInvoice.status === 'paid') {
                    setUser({
                        ...user, store: {
                            ...user?.store,
                            status: subscriptionStatus.ACTIVE,
                            status: storeStatus.ACTIVE
                        }
                    });
                } else if (lastInvoice.status === 'pending') {
                    setPendingInvoice([lastInvoice])
                }
                window.open(`/faturas/${lastInvoice.id}`, '_blank')
            })
            .catch(error => {
                const errorCode = _.get(error, 'error.code', '');
                toast.error(`Não foi possível reativar a assinatura. Por favor, entre em contato com o nosso suporte. (#${errorCode})`);
            })
            .finally(() => {
                setLoadingActivateSubscription(false)
            })
    }

    if (user?.store?.platformId === PLATFORMS.WIX) {
        return (
            <BillingLayout fullScreen={fullScreen}>
                <Box height='100%' alignItems="center" justifyContent='center' flexDirection="column" display="flex">
                    <Grid item xs='auto' mt={2} width={10}>
                        <img className={classes.wixLogo} src={wixLogo} alt="logo" />
                    </Grid>

                    <Typography variant="h6" mt={5} mb={5}>
                        Redirecionando você para o Wix...
                    </Typography>

                    <CircularProgress />
                </Box>
            </BillingLayout>
        )
    }

    if (user?.store?.status === storeStatus.SUSPENDED) {
        if (pendingInvoice.length > 0) {
            return (
                <BillingLayout fullScreen={fullScreen}>
                    <Box textAlign='center' marginY={5}>
                        <Typography variant="h3" className={classes.warning}>
                            Sua assinatura está expirada!
                        </Typography>

                        {loadingPendingInvoice ? (
                            <CircularProgress />
                        ) : (
                            <Typography variant='h5'>
                                Pague a fatura em aberto para reativar os seus aplicativos.
                            </Typography>
                        )}

                    </Box>

                    {!loadingPendingInvoice && (
                        <Box alignItems="center" flexDirection="column" display="flex">
                            <Button
                                variant='contained'
                                onClick={() => window.open(`/faturas/${pendingInvoice[0].id}`, '_blank')}
                            >
                                ver fatura
                            </Button>
                        </Box>
                    )}
                </BillingLayout>
            )
        } else {
            return (
                <BillingLayout fullScreen={fullScreen}>
                    <Box textAlign='center' marginY={5}>
                        <Typography variant="h3" className={classes.warning}>
                            Sua assinatura está expirada!
                        </Typography>

                        {loadingPendingInvoice ? (
                            <CircularProgress />
                        ) : (
                            <Typography variant='h5'>
                                Clique no botão abaixo para gerar uma nova fatura. Após o pagamento, seus aplicativos serão reativados.
                            </Typography>
                        )}

                    </Box>

                    {!loadingPendingInvoice && (
                        <Box alignItems="center" flexDirection="column" display="flex">
                            {loadingActivateSubscription ? (
                                <CircularProgress />
                            ) : (
                                <Button
                                    variant='contained'
                                    onClick={() => activeSubscription()}
                                >
                                    reativar assinatura
                                </Button>
                            )}
                        </Box>
                    )
                    }
                </BillingLayout >
            )
        }
    }

    return (
        <BillingLayout fullScreen={fullScreen} showBackButton={user?.store?.status === storeStatus.ON_TRIAL}>
            <Box textAlign='center' marginY={5}>
                {user.store?.status === "trial_ended" &&
                    <Typography variant="h3" className={classes.warning}>
                        Seu período de testes acabou!
                    </Typography>
                }

                <Typography variant="h5" sx={{ paddingX: '16px' }}>
                    Ative agora sua conta e publique os seus aplicativos
                </Typography>
            </Box>

            <Box alignItems="center" flexDirection="column" display="flex">
                <Box display="flex" flexDirection="row">
                    <PeriodSelector periodicity={periodicity} onSelect={(periodicity) => setPeriodicity(periodicity)} />
                </Box>
                {[PLATFORMS.LOJAINTEGRADA, PLATFORMS.WEBVIEW].includes(user?.store?.platformId)
                    ? <Box mt={4} mb={4}>
                        <PlanCard
                            periodicity={periodicity}
                            planType="basic"
                            platformId={user?.store?.platformId}
                            onSelect={handlePlanSelect}
                            hideMissingFeatures
                        />
                    </Box>
                    : <div className={classes.plansGrid}>
                        <PlanCard
                            periodicity={periodicity}
                            planType="basic"
                            platformId={user?.store?.platformId}
                            onSelect={handlePlanSelect}
                        />
                        <PlanCard
                            periodicity={periodicity}
                            planType="pro"
                            platformId={user?.store?.platformId}
                            onSelect={handlePlanSelect}
                        />
                    </div>
                }
            </Box>

            <div className={classes.disclaimer}>
                <p>Para publicar os seus aplicativos Android ou iOS é necessário criar as seguintes contas:</p>
                <div className="dev-account-wrapper">
                    <div className="dev-account">
                        <img src={GooglePlayLogo} width="134" height="30" alt="Google Play Developer Account" />
                        <span>U$25 (taxa única)</span>
                    </div>

                    <div className="dev-account">
                        <img src={AppleDeveloperLogo} width="134" height="30" alt="Apple Developer Account" />
                        <span>U$99/ano</span>
                    </div>
                </div>
            </div>

            {needsPlatformAuthorization &&
                <AuthorizationModal
                    open={authorizationState !== 'closed'}
                    platformId={user?.store?.platformId}
                    redirecting={authorizationState === 'redirecting'}
                    storeUrl={user?.store?.storeUrl}
                    onRedirecting={() => setAuthorizationState('redirecting')}
                    onClose={closeAuthorizationModal}
                />
            }
        </BillingLayout>
    )
}